import { useState } from "react";
import { RxBill } from "../types/PatientBills.types";
import useStore from "../../../../helpers/useStore";
import { TransactionsContext } from "../../../../stores/Transactions/Transactions.provider";
import {
  CreateBankTransferDto,
  PatientBillInfoDto,
} from "../../../../api-client";
import {
  recnungTransactionMethods,
  transactionTypes,
} from "../../../Transactions/Kasse/v2/types/kasse.types";
import notificationStore from "../../../../stores/Notification/notificationStore";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  List,
  ListItem,
  ListItemText,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useCreateBankTransfer } from "../../../../stores/Transactions/transaction.mutations";
import { usePatientExistingRxData } from "../hooks/usePatientExistingRxData";
import { format } from "date-fns";
import {
  useLocationId,
  useUserBusinessId,
} from "../../../../stores/User/User.hooks";

interface NewExternProps {
  open: boolean;
  userId: string;
  patientId: number;
  rxId: string;
  onPaymentSuccess: () => void;
}

const currency = new Intl.NumberFormat(undefined, {
  style: "currency",
  currency: "EUR",
});

function BillSummary({
  bill,
  patientId,
}: {
  bill?: PatientBillInfoDto | null;
  patientId: number;
}) {
  if (!bill) return <Typography variant="body2">No data</Typography>;
  const date = bill.date ? format(new Date(bill.date), "dd.MM.yyyy") : "—";
  return (
    <List dense disablePadding>
      <ListItem disableGutters>
        <ListItemText primary="Patient ID" secondary={patientId} />
      </ListItem>
      <ListItem disableGutters>
        <ListItemText primary="Rx number" secondary={bill.rxNumber} />
      </ListItem>
      <ListItem disableGutters>
        <ListItemText primary="Datum" secondary={date} />
      </ListItem>
      <Divider sx={{ my: 1 }} />
      <ListItem disableGutters>
        <ListItemText
          primary="	Fällig"
          secondary={currency.format(bill.dueAmount ?? 0)}
        />
      </ListItem>
      <Divider sx={{ my: 1 }} />
      <ListItem disableGutters>
        <ListItemText primary="Mandant" secondary={bill.accountId} />
      </ListItem>
      <ListItem disableGutters>
        <ListItemText primary="Tarifgruppe" secondary={bill.tarifGroupId} />
      </ListItem>
      <Divider sx={{ my: 1 }} />
      <ListItem disableGutters>
        <Stack
          direction="column"
          spacing={1}
          sx={{ width: "100%", justifyContent: "space-between" }}
        >
          <Typography variant="body2">Anzahl der Therapien</Typography>
          <Typography variant="body2">
            {Array.isArray(bill.therapies) ? `${bill.therapies.length}` : "—"}
          </Typography>
        </Stack>
      </ListItem>
    </List>
  );
}

export const NewExternalBalanceDialog: React.FC<NewExternProps> = ({
  open,
  userId,
  patientId,
  onPaymentSuccess,
  rxId,
}) => {
  const createBankTransferMut = useCreateBankTransfer();
  const {
    data: bill,
    isLoading,
    isError,
  } = usePatientExistingRxData(patientId, rxId);

  const businessId = useUserBusinessId();
  const locationId = useLocationId();
  const [payAmount, setPayAmount] = useState<number>(bill?.dueAmount || 0);
  const [note, setNote] = useState<string>("Extern-abgerechnet");

  const handleSubmit = () => {
    const dto: CreateBankTransferDto = {
      method: recnungTransactionMethods.BANK_TRANSFER,
      text: note,
      type: transactionTypes.EINNAHME,
      amount: payAmount,
      rechnungAllocations: [],
      accountId: "KH THEORG",
      userId: userId || "",
      patientId: patientId,
      rxId: rxId,
      businessId: businessId,
      locationId: locationId,
    };

    createBankTransferMut.mutate(dto, {
      onSuccess: () => {
        onPaymentSuccess();
      },
    });
  };

  return (
    <Dialog open={open} onClose={onPaymentSuccess} fullWidth maxWidth="sm">
      <DialogTitle>Extern-abgerechnet</DialogTitle>
      <DialogContent>
        <BillSummary bill={bill} patientId={patientId} />
        <TextField
          label="Note"
          fullWidth
          margin="normal"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          disabled={isLoading}
        />
        <TextField
          label="Amount"
          type="number"
          fullWidth
          margin="normal"
          value={payAmount}
          onChange={(e) => setPayAmount(Number(e.target.value))}
          disabled={isLoading}
        />
        {isError && <p style={{ color: "red" }}>Error: {isError}</p>}
      </DialogContent>
      <DialogActions>
        <Button onClick={onPaymentSuccess} disabled={isLoading}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={isLoading || !note}
          variant="contained"
        >
          {isLoading ? "Updating..." : "Submit"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
